import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Mail, AlertTriangle, Calendar, FileText, Loader2, Check, X, Smartphone } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getCurrentSubscription, getPushPermission } from '../utils/pushNotifications';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NotificationSettings - Varslingsinnstillinger-side
 * Lar brukere kontrollere hvilke e-postvarsler de mottar
 */
const NotificationSettings = () => {
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [settings, setSettings] = useState({
    email_enabled: true,
    ruh_alerts: true,
    task_reminders: true,
    overdue_alerts: true,
    weekly_summary: true,
    reactivation_nudge: true
  });
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    checkPushStatus();
  }, []);
  
  const checkPushStatus = async () => {
    const supported = await isPushSupported();
    setPushSupported(supported);
    if (supported) {
      const perm = await getPushPermission();
      setPushPermission(perm);
      const sub = await getCurrentSubscription();
      setPushEnabled(!!sub);
    }
  };
  
  const togglePush = async (enable) => {
    setPushLoading(true);
    try {
      if (enable) {
        const sub = await subscribeToPush();
        if (sub) {
          setPushEnabled(true);
          setPushPermission('granted');
          toast.success(isNorwegian ? 'Push-varsler aktivert' : 'Push notifications enabled');
        } else {
          const perm = await getPushPermission();
          setPushPermission(perm);
          if (perm === 'denied') {
            toast.error(isNorwegian 
              ? 'Varsler er blokkert i nettleseren. Aktiver dem i nettleserinnstillingene.' 
              : 'Notifications are blocked. Enable them in browser settings.');
          }
        }
      } else {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success(isNorwegian ? 'Push-varsler deaktivert' : 'Push notifications disabled');
      }
    } catch (e) {
      console.error('Push toggle error:', e);
      toast.error(isNorwegian ? 'Noe gikk galt' : 'Something went wrong');
    } finally {
      setPushLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await fetch(`${API_URL}/api/v2/ops/users/me/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.notification_settings);
      }
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    // If trying to disable main email toggle, show confirmation
    if (key === 'email_enabled' && value === false) {
      setShowDisableConfirm(true);
      return;
    }
    
    await performUpdate(key, value);
  };

  const performUpdate = async (key, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await fetch(`${API_URL}/api/v2/ops/users/me/notifications`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
      
      if (response.ok) {
        setSettings(prev => ({ ...prev, [key]: value }));
        toast.success(isNorwegian ? 'Innstilling oppdatert' : 'Setting updated');
      } else {
        toast.error(isNorwegian ? 'Kunne ikke oppdatere' : 'Failed to update');
      }
    } catch (error) {
      console.error('Failed to update setting:', error);
      toast.error(isNorwegian ? 'Noe gikk galt' : 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDisableAll = async () => {
    setShowDisableConfirm(false);
    await performUpdate('email_enabled', false);
  };

  const notificationOptions = [
    {
      key: 'email_enabled',
      icon: Mail,
      title: isNorwegian ? 'E-postvarsler' : 'Email Notifications',
      description: isNorwegian 
        ? 'Aktiver eller deaktiver alle e-postvarsler' 
        : 'Enable or disable all email notifications',
      helpText: isNorwegian
        ? '⚠️ Slår du av denne, deaktiveres alle varslene under automatisk'
        : '⚠️ Turning this off will automatically disable all notifications below',
      isPrimary: true
    },
    {
      key: 'ruh_alerts',
      icon: AlertTriangle,
      title: isNorwegian ? 'RUH-varsler' : 'RUH Alerts',
      description: isNorwegian 
        ? 'Få varsel når nye RUH-hendelser registreres' 
        : 'Get notified when new RUH incidents are registered'
    },
    {
      key: 'task_reminders',
      icon: Calendar,
      title: isNorwegian ? 'Oppgavepåminnelser' : 'Task Reminders',
      description: isNorwegian 
        ? 'Påminnelser 2 dager før oppgavefrister' 
        : 'Reminders 2 days before task deadlines'
    },
    {
      key: 'overdue_alerts',
      icon: AlertTriangle,
      title: isNorwegian ? 'Forfalte oppgaver' : 'Overdue Alerts',
      description: isNorwegian 
        ? 'Daglige varsler om forfalte oppgaver' 
        : 'Daily alerts about overdue tasks'
    },
    {
      key: 'weekly_summary',
      icon: FileText,
      title: isNorwegian ? 'Ukentlig oppsummering' : 'Weekly Summary',
      description: isNorwegian 
        ? 'Statusrapport hver mandag morgen' 
        : 'Status report every Monday morning'
    },
    {
      key: 'reactivation_nudge',
      icon: Bell,
      title: isNorwegian
        ? 'Påminnelse ved inaktivitet'
        : 'Reactivation reminder',
      description: isNorwegian
        ? 'Send meg ukentlig påminnelse hvis jeg ikke har vært inne'
        : 'Send me a weekly reminder if I haven\'t checked in'
    }
  ];

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Bell className="w-8 h-8 text-cyan-500" />
            <h1 className="text-2xl font-bold text-white">
              {isNorwegian ? 'Varslingsinnstillinger' : 'Notification Settings'}
            </h1>
          </div>
          <p className="text-slate-400">
            {isNorwegian 
              ? 'Velg hvilke e-postvarsler du ønsker å motta' 
              : 'Choose which email notifications you want to receive'}
          </p>
        </div>

        {/* Settings List */}
        <div className="space-y-4">
          {notificationOptions.map((option) => {
            const Icon = option.icon;
            const isDisabled = !settings.email_enabled && !option.isPrimary;
            
            return (
              <div 
                key={option.key}
                data-testid={`notification-${option.key}`}
                className={`
                  bg-slate-900 border border-slate-800 rounded-xl p-5
                  transition-all duration-200
                  ${option.isPrimary ? 'border-cyan-500/30' : ''}
                  ${isDisabled ? 'opacity-50' : ''}
                `}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`
                      p-2.5 rounded-lg
                      ${option.isPrimary ? 'bg-cyan-500/20' : 'bg-slate-800'}
                    `}>
                      <Icon className={`w-5 h-5 ${option.isPrimary ? 'text-cyan-400' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">{option.title}</h3>
                      <p className="text-sm text-slate-400">{option.description}</p>
                      {option.helpText && settings.email_enabled && (
                        <p className="text-xs text-amber-400/80 mt-2">{option.helpText}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium ${settings[option.key] ? 'text-cyan-400' : 'text-slate-500'}`}>
                      {settings[option.key] 
                        ? (isNorwegian ? 'PÅ' : 'ON') 
                        : (isNorwegian ? 'AV' : 'OFF')}
                    </span>
                    <Switch
                      checked={settings[option.key]}
                      onCheckedChange={(checked) => updateSetting(option.key, checked)}
                      disabled={isDisabled || saving}
                      data-testid={`switch-${option.key}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Push Notifications Section */}
        {pushSupported && (
          <div className="mt-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-cyan-500" />
              {isNorwegian ? 'Push-varsler i nettleseren' : 'Browser Push Notifications'}
            </h2>
            <div
              data-testid="notification-push-enabled"
              className={`bg-slate-900 border rounded-xl p-5 transition-all duration-200 ${
                pushEnabled ? 'border-cyan-500/30' : 'border-slate-800'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`p-2.5 rounded-lg ${pushEnabled ? 'bg-cyan-500/20' : 'bg-slate-800'}`}>
                    <Bell className={`w-5 h-5 ${pushEnabled ? 'text-cyan-400' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">
                      {isNorwegian ? 'Push-varsler' : 'Push Notifications'}
                    </h3>
                    <p className="text-sm text-slate-400">
                      {isNorwegian
                        ? 'Motta varsler direkte i nettleseren - også når du ikke har appen åpen'
                        : 'Receive alerts directly in your browser - even when the app is not open'}
                    </p>
                    {pushPermission === 'denied' && (
                      <p className="text-xs text-rose-400 mt-2">
                        {isNorwegian
                          ? 'Varsler er blokkert. Gå til nettleserinnstillinger for å aktivere.'
                          : 'Notifications are blocked. Go to browser settings to enable.'}
                      </p>
                    )}
                    {pushEnabled && (
                      <p className="text-xs text-emerald-400 mt-2">
                        {isNorwegian
                          ? 'Du mottar push-varsler for nye RUH, oppgavepåminnelser og forfalte oppgaver'
                          : 'You receive push alerts for new RUH, task reminders and overdue tasks'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${pushEnabled ? 'text-cyan-400' : 'text-slate-500'}`}>
                    {pushEnabled ? (isNorwegian ? 'PÅ' : 'ON') : (isNorwegian ? 'AV' : 'OFF')}
                  </span>
                  <Switch
                    checked={pushEnabled}
                    onCheckedChange={togglePush}
                    disabled={pushLoading || pushPermission === 'denied'}
                    data-testid="switch-push-enabled"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disabled All Warning Banner */}
        {!settings.email_enabled && (
          <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <X className="w-5 h-5 text-amber-400" />
              <p className="text-sm text-amber-300">
                {isNorwegian 
                  ? 'Alle e-postvarsler er deaktivert. Du vil ikke motta noen varsler før du aktiverer dem igjen.' 
                  : 'All email notifications are disabled. You will not receive any alerts until you enable them again.'}
              </p>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-slate-900/50 border border-slate-800 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-cyan-500/10 rounded-lg">
              <Check className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <h4 className="text-white font-medium mb-1">
                {isNorwegian ? 'Automatiske påminnelser' : 'Automatic Reminders'}
              </h4>
              <p className="text-sm text-slate-400">
                {isNorwegian 
                  ? 'Systemet sender automatisk påminnelser basert på dine innstillinger. Du trenger ikke å gjøre noe annet.' 
                  : 'The system automatically sends reminders based on your settings. You don\'t need to do anything else.'}
              </p>
            </div>
          </div>
        </div>

        {/* Schedule Info */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>
            {isNorwegian 
              ? '📅 Påminnelser sendes kl. 08:00 • Ukentlig oppsummering sendes mandager kl. 08:00' 
              : '📅 Reminders sent at 08:00 • Weekly summary sent Mondays at 08:00'}
          </p>
        </div>
      </div>

      {/* Confirmation Dialog for Disabling All Notifications */}
      <AlertDialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              {isNorwegian ? 'Deaktivere alle varsler?' : 'Disable all notifications?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {isNorwegian 
                ? 'Når du slår av e-postvarsler, vil alle varsler bli deaktivert. Du vil ikke motta noen varsler om RUH-hendelser, oppgaver eller påminnelser før du aktiverer dem igjen.' 
                : 'When you turn off email notifications, all alerts will be disabled. You will not receive any notifications about RUH incidents, tasks, or reminders until you enable them again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700 hover:text-white">
              {isNorwegian ? 'Avbryt' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDisableAll}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {isNorwegian ? 'Ja, deaktiver alle' : 'Yes, disable all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
};

export default NotificationSettings;
